// Events Release B (#681): the booking fee, in the browser. Money travels as whole CENTS on the wire;
// admins type DOLLARS. The server is the authority on the amount a family is charged: this file only
// converts what an admin types and checks it before sending (the server checks again).

/** Mirrors the server cap (BOOKING_FEE_MAX_CENTS): $1,000 per ticket or per booking. A typo guard, not a business rule. */
export const BOOKING_FEE_MAX_CENTS = 100000;

/** "3", "3.5" or "3.50" as 300, 350, 350; anything else (blank, "abc", "3.505", "-1") is null. */
export function dollarsToCents(text: string): number | null {
  const t = text.trim().replace(/^\$/, '');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(parseFloat(t) * 100);
}

/** 350 -> "3.50" (what the text box shows). */
export function centsToDollarsText(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** The message under the amount box, or null when it is fine. */
export function bookingFeeAmountError(text: string): string | null {
  const cents = dollarsToCents(text);
  if (cents === null) return 'Enter an amount like 3.50';
  if (cents > BOOKING_FEE_MAX_CENTS) return 'That is more than $1,000';
  return null;
}

/**
 * How many PAID tickets a cart holds: tickets whose tier price is above 0. The booking fee is charged per
 * paid ticket only (Andrew, 04/11/2026), the same count the server uses.
 */
export function paidTicketCount(
  tiers: Array<{ id: string; priceCents: number }>,
  quantities: Record<string, number>
): number {
  return tiers.reduce((sum, t) => (t.priceCents > 0 ? sum + Math.max(0, Math.floor(quantities[t.id] || 0)) : sum), 0);
}

export type FeeMode = 'PER_TICKET' | 'PER_BOOKING';

/** "per paid ticket" / "per booking": the unit an amount is charged by (a $0 ticket type never pays it). */
export function bookingFeeUnitText(mode: FeeMode | undefined | null): string {
  return mode === 'PER_BOOKING' ? 'per booking' : 'per paid ticket';
}

/** The event editor's three booking fee choices: the school setting, no fee, or the event's own fee. */
export type BookingFeeChoice = 'SCHOOL' | 'NONE' | 'CUSTOM';

/**
 * An event's two stored overrides read back as the editor's choice. The same three meanings as the server's
 * `resolveBookingFee` (review M1): null = the school setting (amount AND mode; a stray stored mode is ignored, so it
 * is not shown), 0 = no fee, n = a custom fee whose mode is the stored one ('' = like the school).
 */
export function bookingFeeChoiceOf(
  amountOverride: number | null | undefined,
  modeOverride: FeeMode | null | undefined
): { choice: BookingFeeChoice; dollars: string; customMode: '' | FeeMode } {
  if (amountOverride == null) return { choice: 'SCHOOL', dollars: '', customMode: '' };
  if (amountOverride <= 0) return { choice: 'NONE', dollars: '', customMode: '' };
  return { choice: 'CUSTOM', dollars: centsToDollarsText(amountOverride), customMode: modeOverride ?? '' };
}

/**
 * The two overrides the editor SENDS for a choice. A mode only travels with a custom amount, the rule the server
 * stores by (`storedBookingFeeModeOverride`, review M1): School setting -> (null, null), No fee -> (0, null),
 * Custom -> (cents, its mode or null). A free event sends neither (absent = unchanged): it never charges a fee.
 */
export function bookingFeeOverridesFor(input: {
  isFree: boolean;
  choice: BookingFeeChoice;
  dollars: string;
  customMode: '' | FeeMode;
}): { bookingFeeCentsOverride: number | null | undefined; bookingFeeModeOverride: FeeMode | null | undefined } {
  if (input.isFree) return { bookingFeeCentsOverride: undefined, bookingFeeModeOverride: undefined };
  if (input.choice === 'SCHOOL') return { bookingFeeCentsOverride: null, bookingFeeModeOverride: null };
  if (input.choice === 'NONE') return { bookingFeeCentsOverride: 0, bookingFeeModeOverride: null };
  const cents = dollarsToCents(input.dollars);
  return {
    bookingFeeCentsOverride: cents,
    bookingFeeModeOverride: cents !== null && cents > 0 && input.customMode !== '' ? input.customMode : null,
  };
}

/**
 * What a buyer pays on top of the tickets, for `paidTickets` PAID tickets. `fee` is the server's computed
 * `bookingFee` ({ mode, amountCents }; amountCents 0 = none). PER_TICKET: the amount for every paid ticket.
 * PER_BOOKING: the amount once, when the order holds at least one paid ticket. The same rule as the server's
 * `bookingFeeTotalFor`; display only, the server charges its own figure.
 */
export function bookingFeeTotalCents(
  fee: { mode: FeeMode; amountCents: number } | undefined | null,
  paidTickets: number
): number {
  const amount = fee && Number.isInteger(fee.amountCents) && fee.amountCents > 0 ? fee.amountCents : 0;
  const count = Math.max(0, Math.floor(paidTickets));
  if (amount === 0 || count < 1) return 0;
  return fee?.mode === 'PER_BOOKING' ? amount : amount * count;
}
